import { h, ref } from 'vue'
import { i18nRender } from '@/locales'
import i18n from '@/locales'
import { Modal } from 'ant-design-vue'
import type {
  Condition,
  Verifier,
  VerifyProof,
  FlowUI,
  VerifierRunner,
  Step,
  VerifyNode,
  GroupCondition,
} from './types'
import MfaStepsContent from './verifierComponents/MfaStepsContent.vue'
import { apiSecurityBegin } from './api'
import type { SecurityBeginData } from './types'
import { http } from '@/libs/http'
import { useModalManagerStore } from '@/store'
import { registerCommonModals } from '@/modals/common'

export const registry = new Map<string, () => Promise<Verifier | { default: Verifier }>>()
const tr = (key: string, params?: Record<string, any>) => (i18n.global as any).t(key, params ?? {})
export function registerVerifier(
  type: string,
  loader: () => Promise<Verifier | { default: Verifier }>,
) {
  registry.set(type, loader)
}

export const isGroupCondition = (node: VerifyNode): node is GroupCondition =>
  (node as GroupCondition).operator !== undefined &&
  Array.isArray((node as GroupCondition).conditions)

export const isLeafCondition = (node: VerifyNode): node is Condition =>
  (node as Condition).type !== undefined

function toVerifier(mod: Verifier | { default: Verifier }): Verifier {
  const v = (mod as any)?.verify ? (mod as Verifier) : (mod as any)?.default
  if (!v || typeof v.verify !== 'function') {
    throw new Error(i18nRender('components.security.mfa.orchestrator.invalidVerifier'))
  }
  return v
}

const getCurrentVerifyPolicy = (steps: Step[]) => {
  if (steps.length === 0) return {}
  const noNeedVerifyPolicy = ['email_validate']
  const mustVerifyPolicy: Set<string> = new Set()
  const optionalVerifyPolicy: Set<string> = new Set()
  for (const { operator, conditions } of steps) {
    if (operator === 'and') {
      for (const { type } of conditions) {
        if (noNeedVerifyPolicy.includes(type)) continue
        mustVerifyPolicy.add(type)
      }
    } else if (operator === 'or') {
      for (const { type } of conditions) {
        if (noNeedVerifyPolicy.includes(type)) continue
        if (mustVerifyPolicy.has(type)) continue
        optionalVerifyPolicy.add(type)
      }
    }
  }
  return {
    mustVerifyPolicy,
    optionalVerifyPolicy,
  }
}

function hasCancel(v: unknown): v is { cancel: (ctx: any) => any } {
  return !!v && typeof (v as any).cancel === 'function'
}

function resolveKind(cond: Condition, verifier?: Verifier): VerifierRunner['kind'] {
  const k = (verifier as any)?.kind
  if (k) return k as VerifierRunner['kind']
  switch (cond.type) {
    case 'sms_validate':
      return 'sms' as VerifierRunner['kind']
    case 'email_validate':
      return 'email' as VerifierRunner['kind']
    case 'app_validate':
      return 'app' as VerifierRunner['kind']
    case 'totp_validate':
      return 'totp' as VerifierRunner['kind']
    case 'biometric_validate':
      return 'webauthn' as VerifierRunner['kind']
    default:
      return cond.type as unknown as VerifierRunner['kind']
  }
}

const isPassed = (v: unknown) => String(v) === '1'

const normalizeOperator = (op: unknown): 'and' | 'or' => {
  const s = String(op || '').toLowerCase()
  return s === 'and' ? 'and' : 'or'
}

export async function runSecurityFlow(input: {
  securityCode: string
  title?: string
  credentialType: -1 | 1 | 2
  payload?: any
  verifyStatusMap?: Record<string, boolean>
}) {
  const { securityCode, credentialType, title, payload, verifyStatusMap } = input
  const { google_passkey, apple_passkey, ...mainVerifyStatus } = verifyStatusMap ?? {}

  const modalStore = useModalManagerStore()
  registerCommonModals()
  let begin: SecurityBeginData
  let mfaCleanFn = null
  let submitCleanFn = null
  let email = undefined
  let phone = undefined
  let loginId = undefined
  let unauthorized = true
  const type = Number(payload?.request?.type ?? -1)
  const loginType = [2, 1][type] ?? -1
  // 未登录参数通过请求头传递
  if (securityCode === 'remote_login') {
    email = payload?.data?.email_text
    phone = payload?.data?.phone_text
    loginId = loginType === 2 ? payload.request.email : payload.request.phone
    unauthorized = false
    const { clear } = http.useScopedHeaders(
      {
        identifierId: loginId,
        channel: loginType,
      },
      {
        autoClearAfterResponse: false,
      },
    )
    mfaCleanFn = clear

    begin = await apiSecurityBegin({
      security_code: securityCode,
      credential_type: credentialType,
    })
  } else {
    // 向后端发起 begin，拿到步骤与 passage_key
    begin = await apiSecurityBegin({
      security_code: securityCode,
      credential_type: credentialType,
    })
  }

  const passageKey = begin.passage_key

  if (isPassed(begin.status)) return { ok: true, passageKey }

  const make = async (cond: Condition): Promise<VerifierRunner> => {
    const loader = registry.get(cond.type)
    if (!loader) {
      throw new Error(
        i18nRender('components.security.mfa.orchestrator.unregistered').replace(
          '{type}',
          String(cond.type),
        ),
      )
    }

    const mod = await loader()
    const verifier = toVerifier(mod)
    const ctx = { condition: cond, passageKey, securityCode }

    const runner: VerifierRunner = {
      kind: resolveKind(cond, verifier),

      start: verifier.start ? () => verifier.start!(ctx) : undefined,

      send: verifier.send ? () => verifier.send!(ctx) : undefined,

      verify: async (proof?: VerifyProof) => {
        const res = await verifier.verify({ ...ctx, proof })
        return typeof res === 'object' ? Boolean((res as any).ok) : Boolean(res)
      },

      cancel: hasCancel(verifier) ? () => (verifier as any).cancel(ctx) : undefined,
    }

    return runner
  }

  const steps = (begin.steps ?? [])
    .map((s: any) => ({
      operator: normalizeOperator(s?.operator),
      conditions: Array.isArray(s?.conditions) ? s.conditions : [],
    }))
    .filter((s: any) => s.conditions.length > 0)

  const { mustVerifyPolicy, optionalVerifyPolicy } = getCurrentVerifyPolicy(steps)
  const boundTypes = [apple_passkey ? 1 : undefined, google_passkey ? 2 : undefined]
    .filter(Boolean)
    .join(',') as string
  // 收集所有前置条件错误信息
  let errorKeys: string[] = []
  let errorParams: Record<string, string> = {}
  const errorMessageMap = {
    biometric_validate: 'components.security.mfa.orchestrator.messages.passkeyRequired',
    sms_validate: 'components.security.mfa.orchestrator.messages.smsRequired',
    app_validate: 'components.security.mfa.orchestrator.messages.appRequired',
    google_passkey: 'components.security.mfa.orchestrator.messages.googlePasskey',
    apple_passkey: 'components.security.mfa.orchestrator.messages.applePasskey',
  }
  // 先必验校验
  if (mustVerifyPolicy.size > 0) {
    // 先检查Passkey，它是必验的
    if (mustVerifyPolicy.has('biometric_validate')) {
      if (!mainVerifyStatus.biometric_validate || credentialType === -1) {
        errorKeys.push(errorMessageMap.biometric_validate)
      } else if (!boundTypes.includes(String(credentialType))) {
        const envText = tr(
          credentialType === 1
            ? 'components.security.mfa.orchestrator.messages.applePasskey'
            : 'components.security.mfa.orchestrator.prereqMessages.googlePasskey',
        )
        const curText = tr(
          credentialType === 1
            ? 'components.security.mfa.orchestrator.messages.applePasskey'
            : credentialType === 2
              ? 'components.security.mfa.orchestrator.messages.googlePasskey'
              : 'components.security.mfa.orchestrator.messages.unspecified',
        )
        errorKeys.push(errorMessageMap.biometric_validate)
        errorParams = { envText, curText }
      }
    }
    // 检查短信验证
    if (mustVerifyPolicy.has('sms_validate') && !mainVerifyStatus.sms_validate) {
      errorKeys.push(errorMessageMap.sms_validate)
    }
    // 检查验证器 App
    if (mustVerifyPolicy.has('app_validate') && !mainVerifyStatus.app_validate) {
      errorKeys.push(errorMessageMap.app_validate)
    }
  }

  // 再选验校验，Passkey是必验的
  if (optionalVerifyPolicy.size > 0) {
    const optionalPolicies = Array.from(optionalVerifyPolicy)
    const allNotSet = optionalPolicies.every((policy) => {
      return mainVerifyStatus[policy]
    })
    if (!allNotSet) {
      errorKeys.push('components.security.mfa.orchestrator.messages.oneOfRequired')
      errorParams = {
        ...errorParams,
        policy: optionalPolicies
          .map((policy) => tr(`components.security.mfa.orchestrator.messages.${policy}`))
          .join('、'),
      }
    }
  }

  // 如果有错误，统一弹窗提示
  if (errorKeys.length > 0) {
    const errorMessages: string[] = errorKeys.map((key) => tr(key, errorParams))
    const errorMessage = errorMessages
      .map((message, index) => ` ${index + 1}. ${message}`)
      .join('\n')
    try {
      const response = await modalStore.open('RemoteLoginWarningModal', {
        title: tr('components.security.mfa.orchestrator.prereqTitle'),
        message: errorMessage,
      })
      if (response.ok) {
        const modalId = (response.data as any)?.modalId
        modalStore.operationSuccess(modalId, { success: true })
      }
    } catch (error) {
      console.error('Modal operation failed:', error)
    }
    return { ok: false }
  }

  const ok: boolean = await (async (): Promise<boolean> => {
    const t = (i18n.global as any).t
    const contentRef = ref<any>(null)

    return new Promise<boolean>((resolve) => {
      const m = Modal.confirm({
        title: title ?? t('components.security.mfa.flowTitle', '二次验证'),
        wrapClassName: 'mfa-modal',
        closable: true,
        width: 480,
        icon: null,
        footer: null,
        content: () =>
          h(MfaStepsContent, {
            ref: (el: any) => (contentRef.value = el),
            steps,
            passageKey,
            securityCode,
            make,
            verifyData: { email, phone, loginType, unauthorized },
            onSubmit: () => {
              // 设置一次性请求头
              const { clear } = http.useScopedHeaders(
                { passageKey },
                { autoClearAfterResponse: false },
              )
              submitCleanFn = clear
              resolve(true)
              m.destroy()
            },
          }),
      })
    })
  })()
  if (!ok) {
    throw new Error(i18nRender('components.security.mfa.orchestrator.notPassed'))
  }
  mfaCleanFn?.()
  return { ok: true, passageKey, submitCleanFn }
}
